import { Mail } from 'lucide-react';

import SlackIcon from '@/assets/icons/SlackIcon';
import { Button } from '@/components/shadcn/ui/button';
import { InputGroup } from '@/components/shadcn/ui/input-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/ui/select';
import { Stage } from '@/components/Stage/Stage';
import { getFieldError } from '@/shared/ui/form/get-field-error';
import { Tooltip } from '@/shared/ui/tooltip';
import { WORKFLOW_CHANNEL_TYPE } from '@/types/workflowConst';

import { useSlackWebhook } from './use-slack-webhook';

type NotificationChannelFormProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  workspaceId: string;
  hideEmailOption?: boolean;
  // Test connection 버튼 렌더링 (채널 입력이 유효한지 여부를 받아서 버튼 반환)
  renderTestConnection?: (isChannelValid: boolean) => React.ReactNode;
};

/**
 * FIXME: form options을 공통 schema를 가져가도록 수정하기
 * Notification Channel 선택 컴포넌트 (TanStack Form)
 *
 * 모든 workflow에서 공통으로 사용 가능한 channel 입력 컴포넌트
 * 부모 form의 channel 필드에 바인딩 — channel 필드만 사용
 * withForm 대신 직접 form prop을 받아 여러 form 타입과 호환
 */
export const NotificationChannelForm = ({
  form,
  workspaceId,
  hideEmailOption,
  renderTestConnection,
}: NotificationChannelFormProps) => {
  const { slackChannels, connectSlack } = useSlackWebhook({ workspaceId });

  // disabled 버튼은 pointer-events가 없으므로 span wrapper로 hover 이벤트 전달
  const renderTestConnectionWithTooltip = (
    isValid: boolean,
    tooltipContent: string,
  ) => {
    if (!renderTestConnection) return null;
    return (
      <Tooltip content={tooltipContent} disabled={isValid}>
        <span className="inline-flex w-fit">
          {renderTestConnection(isValid)}
        </span>
      </Tooltip>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Slack/Email 토글 — hideEmailOption이 true이면 토글 숨김 */}
      {!hideEmailOption && (
        <form.AppField
          name="channel.type"
          children={(field: any) => (
            <field.ToggleGroup
              options={[
                { value: WORKFLOW_CHANNEL_TYPE.SLACK, label: 'Slack' },
                { value: WORKFLOW_CHANNEL_TYPE.MAIL, label: 'Email' },
              ]}
              size="sm"
              className="w-fit"
              showErrorMessage={false}
            />
          )}
        />
      )}

      {/* 채널 타입별 입력 */}
      <form.Subscribe
        selector={(state: any) => ({
          channelType: state.values.channel.type as string,
        })}
      >
        {({ channelType }: { channelType: string }) => (
          <Stage>
            {/* Slack 채널 선택 */}
            <Stage.Scene status={channelType === WORKFLOW_CHANNEL_TYPE.SLACK}>
              <div className="flex flex-col gap-2">
                {slackChannels.length > 0 ? (
                  <form.AppField
                    name="channel.slack_webhook_id"
                    validators={{
                      onChange: ({ value }: { value: string }) =>
                        !value ? 'Please select a channel' : undefined,
                      onSubmit: ({ value }: { value: string }) =>
                        !value ? 'Please select a channel' : undefined,
                    }}
                    children={(field: any) => (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-start gap-1">
                          <div className="size-5 mt-2 shrink-0">
                            <SlackIcon size="20px" />
                          </div>
                          <div className="space-y-2">
                            <Select
                              value={field.state.value || undefined}
                              onValueChange={(v: string) =>
                                field.handleChange(v)
                              }
                            >
                              <SelectTrigger className="min-w-[160px] max-w-[210px] *:data-[slot=select-value]:block *:data-[slot=select-value]:truncate">
                                <SelectValue placeholder="Select slack channel..." />
                              </SelectTrigger>
                              <SelectContent>
                                {slackChannels.map((channel: any) => (
                                  <SelectItem
                                    key={channel.alias}
                                    value={channel.alias}
                                  >
                                    {`${channel.channel_name} — ${channel.team_name}`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div className="space-y-1">
                              {renderTestConnectionWithTooltip(
                                !!field.state.value &&
                                  field.state.meta.errors.length === 0,
                                'Please select a Slack channel first',
                              )}
                              <Button
                                variant="link"
                                size="xs"
                                onClick={connectSlack}
                              >
                                Need another Slack channel?
                              </Button>
                            </div>
                            {field.state.meta.errors.length > 0 && (
                              <span className="text-destructive text-sm">
                                {getFieldError(field.state.meta.errors[0])}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  />
                ) : (
                  <div>
                    <Button
                      variant="outline"
                      className="shrink-0"
                      onClick={connectSlack}
                    >
                      <SlackIcon size="16px" />
                      Connect
                    </Button>
                  </div>
                )}
              </div>
            </Stage.Scene>

            {/* Email 입력 */}
            <Stage.Scene status={channelType === WORKFLOW_CHANNEL_TYPE.MAIL}>
              <div className="flex flex-col gap-2">
                <form.AppField
                  name="channel.mail_address"
                  children={(field: any) => (
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-1">
                        <div className="shrink-0 mt-2">
                          <Mail className="size-4 text-muted-foreground" />
                        </div>
                        <div className="space-y-2">
                          <InputGroup className="max-w-[300px]">
                            <field.InputGroupInput
                              type="email"
                              placeholder="Enter email address"
                              showErrorMessage={false}
                            />
                          </InputGroup>
                          {renderTestConnectionWithTooltip(
                            !!field.state.value &&
                              field.state.meta.errors.length === 0,
                            'Please enter a valid email address first',
                          )}
                          {field.state.meta.errors.length > 0 && (
                            <span className="text-destructive text-sm">
                              {getFieldError(field.state.meta.errors[0])}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                />
              </div>
            </Stage.Scene>
          </Stage>
        )}
      </form.Subscribe>
    </div>
  );
};
